/**
 * StateLandmarkIcons.ts
 * 50 US States + DC Bespoke 2.5D Landmark & Wonder SVG Iconography (`viewBox="0 0 48 48"`).
 * Every state features an authentic, recognizable 2.5D isometric/diorama landmark illustration
 * with unique scoped `<defs>` linearGradients (`lm_<code>_*`), specular highlights, and `#1e1b2e` cel-shaded outlines.
 */

import { renderSvg, IconRenderOptions } from './IconRenderer';

export type StateCode51 =
  | 'AL' | 'AK' | 'AZ' | 'AR' | 'CA' | 'CO' | 'CT' | 'DE' | 'FL' | 'GA'
  | 'HI' | 'ID' | 'IL' | 'IN' | 'IA' | 'KS' | 'KY' | 'LA' | 'ME' | 'MD'
  | 'MA' | 'MI' | 'MN' | 'MS' | 'MO' | 'MT' | 'NE' | 'NV' | 'NH' | 'NJ'
  | 'NM' | 'NY' | 'NC' | 'ND' | 'OH' | 'OK' | 'OR' | 'PA' | 'RI' | 'SC'
  | 'SD' | 'TN' | 'TX' | 'UT' | 'VT' | 'VA' | 'WA' | 'WV' | 'WI' | 'WY'
  | 'DC';

export interface StateLandmarkMetadata {
  code: StateCode51;
  stateName: string;
  landmarkName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  archetype:
    | 'BRIDGE_ARCH'
    | 'LIGHTHOUSE_COAST'
    | 'MOUNTAIN_CANYON'
    | 'SPACE_TOWER'
    | 'CAPITOL_MONUMENT'
    | 'HEARTLAND_HERITAGE';
}

export const STATE_LANDMARK_METADATA: Record<StateCode51, StateLandmarkMetadata> = {
  AL: { code: 'AL', stateName: 'Alabama', landmarkName: 'U.S. Space & Rocket Center & Edmund Pettus Bridge', primaryColor: '#ef4444', secondaryColor: '#e2e8f0', accentColor: '#38bdf8', archetype: 'SPACE_TOWER' },
  AK: { code: 'AK', stateName: 'Alaska', landmarkName: 'Denali Peak & Northern Lights', primaryColor: '#38bdf8', secondaryColor: '#818cf8', accentColor: '#34d399', archetype: 'MOUNTAIN_CANYON' },
  AZ: { code: 'AZ', stateName: 'Arizona', landmarkName: 'Grand Canyon & Saguaro Desert', primaryColor: '#ea580c', secondaryColor: '#f59e0b', accentColor: '#22c55e', archetype: 'MOUNTAIN_CANYON' },
  AR: { code: 'AR', stateName: 'Arkansas', landmarkName: 'Hot Springs Bathhouse & Ouachita Diamond', primaryColor: '#10b981', secondaryColor: '#f43f5e', accentColor: '#fde047', archetype: 'MOUNTAIN_CANYON' },
  CA: { code: 'CA', stateName: 'California', landmarkName: 'Golden Gate Bridge', primaryColor: '#e11d48', secondaryColor: '#fb7185', accentColor: '#38bdf8', archetype: 'BRIDGE_ARCH' },
  CO: { code: 'CO', stateName: 'Colorado', landmarkName: 'Rocky Mountains & Red Rocks Amphitheatre', primaryColor: '#6366f1', secondaryColor: '#ea580c', accentColor: '#f8fafc', archetype: 'MOUNTAIN_CANYON' },
  CT: { code: 'CT', stateName: 'Connecticut', landmarkName: 'Mystic Seaport Lighthouse & Tall Ship', primaryColor: '#0284c7', secondaryColor: '#f8fafc', accentColor: '#facc15', archetype: 'LIGHTHOUSE_COAST' },
  DE: { code: 'DE', stateName: 'Delaware', landmarkName: 'Delaware Memorial Twin Bridge & Old New Castle', primaryColor: '#2563eb', secondaryColor: '#93c5fd', accentColor: '#facc15', archetype: 'BRIDGE_ARCH' },
  FL: { code: 'FL', stateName: 'Florida', landmarkName: 'Kennedy Space Rocket & Tropical Palms', primaryColor: '#f97316', secondaryColor: '#ec4899', accentColor: '#06b6d4', archetype: 'SPACE_TOWER' },
  GA: { code: 'GA', stateName: 'Georgia', landmarkName: 'Stone Mountain Dome & Savannah Peach', primaryColor: '#fb923c', secondaryColor: '#16a34a', accentColor: '#fde047', archetype: 'HEARTLAND_HERITAGE' },
  HI: { code: 'HI', stateName: 'Hawaii', landmarkName: 'Diamond Head Crater & Waikiki Wave', primaryColor: '#14b8a6', secondaryColor: '#f43f5e', accentColor: '#facc15', archetype: 'MOUNTAIN_CANYON' },
  ID: { code: 'ID', stateName: 'Idaho', landmarkName: 'Shoshone Falls & Sawtooth Peaks', primaryColor: '#0ea5e9', secondaryColor: '#10b981', accentColor: '#f8fafc', archetype: 'MOUNTAIN_CANYON' },
  IL: { code: 'IL', stateName: 'Illinois', landmarkName: 'Willis Tower & Cloud Gate Bean', primaryColor: '#3b82f6', secondaryColor: '#94a3b8', accentColor: '#facc15', archetype: 'SPACE_TOWER' },
  IN: { code: 'IN', stateName: 'Indiana', landmarkName: 'Indianapolis Speedway Pagoda & Monument Circle', primaryColor: '#dc2626', secondaryColor: '#facc15', accentColor: '#f8fafc', archetype: 'CAPITOL_MONUMENT' },
  IA: { code: 'IA', stateName: 'Iowa', landmarkName: 'Des Moines Golden Capitol Dome & Heartland Barn', primaryColor: '#eab308', secondaryColor: '#ef4444', accentColor: '#22c55e', archetype: 'HEARTLAND_HERITAGE' },
  KS: { code: 'KS', stateName: 'Kansas', landmarkName: 'Monument Rocks Chalk Pyramids & Golden Sunflower', primaryColor: '#f59e0b', secondaryColor: '#fde047', accentColor: '#84cc16', archetype: 'HEARTLAND_HERITAGE' },
  KY: { code: 'KY', stateName: 'Kentucky', landmarkName: 'Churchill Downs Twin Spires & Bluegrass', primaryColor: '#16a34a', secondaryColor: '#f8fafc', accentColor: '#facc15', archetype: 'HEARTLAND_HERITAGE' },
  LA: { code: 'LA', stateName: 'Louisiana', landmarkName: 'French Quarter Balcony & Mississippi Steamboat', primaryColor: '#9333ea', secondaryColor: '#eab308', accentColor: '#10b981', archetype: 'CAPITOL_MONUMENT' },
  ME: { code: 'ME', stateName: 'Maine', landmarkName: 'Portland Head Light & Acadia Pines', primaryColor: '#0284c7', secondaryColor: '#ef4444', accentColor: '#f8fafc', archetype: 'LIGHTHOUSE_COAST' },
  MD: { code: 'MD', stateName: 'Maryland', landmarkName: 'Fort McHenry Star Fort & Chesapeake Lighthouse', primaryColor: '#dc2626', secondaryColor: '#eab308', accentColor: '#38bdf8', archetype: 'LIGHTHOUSE_COAST' },
  MA: { code: 'MA', stateName: 'Massachusetts', landmarkName: 'Old North Steeple & Boston Harbor Light', primaryColor: '#1d4ed8', secondaryColor: '#dc2626', accentColor: '#facc15', archetype: 'LIGHTHOUSE_COAST' },
  MI: { code: 'MI', stateName: 'Michigan', landmarkName: 'Mackinac Suspension Bridge & Great Lakes', primaryColor: '#0284c7', secondaryColor: '#22c55e', accentColor: '#f8fafc', archetype: 'BRIDGE_ARCH' },
  MN: { code: 'MN', stateName: 'Minnesota', landmarkName: 'Split Rock Cliff Lighthouse & North Woods', primaryColor: '#2563eb', secondaryColor: '#f59e0b', accentColor: '#38bdf8', archetype: 'LIGHTHOUSE_COAST' },
  MS: { code: 'MS', stateName: 'Mississippi', landmarkName: 'Delta Paddlewheeler & Magnolia Columns', primaryColor: '#0ea5e9', secondaryColor: '#f43f5e', accentColor: '#fef08a', archetype: 'HEARTLAND_HERITAGE' },
  MO: { code: 'MO', stateName: 'Missouri', landmarkName: 'St. Louis Gateway Arch', primaryColor: '#94a3b8', secondaryColor: '#38bdf8', accentColor: '#facc15', archetype: 'BRIDGE_ARCH' },
  MT: { code: 'MT', stateName: 'Montana', landmarkName: 'Glacier National Park Crown Peaks', primaryColor: '#0284c7', secondaryColor: '#10b981', accentColor: '#f8fafc', archetype: 'MOUNTAIN_CANYON' },
  NE: { code: 'NE', stateName: 'Nebraska', landmarkName: 'Chimney Rock Spire & Prairie Horizon', primaryColor: '#d97706', secondaryColor: '#fbbf24', accentColor: '#38bdf8', archetype: 'MOUNTAIN_CANYON' },
  NV: { code: 'NV', stateName: 'Nevada', landmarkName: 'Hoover Dam & Las Vegas Neon Diamond', primaryColor: '#ec4899', secondaryColor: '#facc15', accentColor: '#38bdf8', archetype: 'BRIDGE_ARCH' },
  NH: { code: 'NH', stateName: 'New Hampshire', landmarkName: 'Mount Washington Alpine Peak & Cog Railway', primaryColor: '#4f46e5', secondaryColor: '#10b981', accentColor: '#f8fafc', archetype: 'MOUNTAIN_CANYON' },
  NJ: { code: 'NJ', stateName: 'New Jersey', landmarkName: 'Cape May Lighthouse & Atlantic Boardwalk', primaryColor: '#0284c7', secondaryColor: '#ef4444', accentColor: '#fde047', archetype: 'LIGHTHOUSE_COAST' },
  NM: { code: 'NM', stateName: 'New Mexico', landmarkName: 'Taos Pueblo Adobe & Zia Sun Symbol', primaryColor: '#ea580c', secondaryColor: '#dc2626', accentColor: '#fde047', archetype: 'HEARTLAND_HERITAGE' },
  NY: { code: 'NY', stateName: 'New York', landmarkName: 'Statue of Liberty & Empire State Spire', primaryColor: '#14b8a6', secondaryColor: '#64748b', accentColor: '#facc15', archetype: 'SPACE_TOWER' },
  NC: { code: 'NC', stateName: 'North Carolina', landmarkName: 'Cape Hatteras Spiral Lighthouse & Kitty Hawk Glider', primaryColor: '#0ea5e9', secondaryColor: '#1e293b', accentColor: '#f8fafc', archetype: 'LIGHTHOUSE_COAST' },
  ND: { code: 'ND', stateName: 'North Dakota', landmarkName: 'Theodore Roosevelt Badlands & Prairie Bison', primaryColor: '#b45309', secondaryColor: '#22c55e', accentColor: '#fde047', archetype: 'MOUNTAIN_CANYON' },
  OH: { code: 'OH', stateName: 'Ohio', landmarkName: 'Rock & Roll Hall of Fame Glass Pyramid', primaryColor: '#dc2626', secondaryColor: '#38bdf8', accentColor: '#f8fafc', archetype: 'SPACE_TOWER' },
  OK: { code: 'OK', stateName: 'Oklahoma', landmarkName: 'Red Earth Oil Derrick & Route 66 Arch', primaryColor: '#c2410c', secondaryColor: '#f59e0b', accentColor: '#fde047', archetype: 'SPACE_TOWER' },
  OR: { code: 'OR', stateName: 'Oregon', landmarkName: 'Crater Lake Wizard Island & Mt. Hood', primaryColor: '#0284c7', secondaryColor: '#15803d', accentColor: '#f8fafc', archetype: 'MOUNTAIN_CANYON' },
  PA: { code: 'PA', stateName: 'Pennsylvania', landmarkName: 'Liberty Bell & Independence Hall', primaryColor: '#d97706', secondaryColor: '#b91c1c', accentColor: '#fde047', archetype: 'CAPITOL_MONUMENT' },
  RI: { code: 'RI', stateName: 'Rhode Island', landmarkName: 'Newport Cliff Walk & Pell Bridge', primaryColor: '#0369a1', secondaryColor: '#38bdf8', accentColor: '#facc15', archetype: 'LIGHTHOUSE_COAST' },
  SC: { code: 'SC', stateName: 'South Carolina', landmarkName: 'Fort Sumter Palmetto & Charleston Harbor', primaryColor: '#1d4ed8', secondaryColor: '#f97316', accentColor: '#f8fafc', archetype: 'LIGHTHOUSE_COAST' },
  SD: { code: 'SD', stateName: 'South Dakota', landmarkName: 'Mount Rushmore Granite Profiles & Black Hills', primaryColor: '#78716c', secondaryColor: '#15803d', accentColor: '#fde047', archetype: 'MOUNTAIN_CANYON' },
  TN: { code: 'TN', stateName: 'Tennessee', landmarkName: 'Great Smoky Mountains & Music City Spires', primaryColor: '#4f46e5', secondaryColor: '#f59e0b', accentColor: '#38bdf8', archetype: 'MOUNTAIN_CANYON' },
  TX: { code: 'TX', stateName: 'Texas', landmarkName: 'The Alamo Mission & Lone Star Longhorn', primaryColor: '#d97706', secondaryColor: '#dc2626', accentColor: '#facc15', archetype: 'CAPITOL_MONUMENT' },
  UT: { code: 'UT', stateName: 'Utah', landmarkName: 'Delicate Arch & Zion Red Cliffs', primaryColor: '#ea580c', secondaryColor: '#f97316', accentColor: '#38bdf8', archetype: 'BRIDGE_ARCH' },
  VT: { code: 'VT', stateName: 'Vermont', landmarkName: 'Green Mountains & Red Covered Bridge', primaryColor: '#15803d', secondaryColor: '#dc2626', accentColor: '#fde047', archetype: 'BRIDGE_ARCH' },
  VA: { code: 'VA', stateName: 'Virginia', landmarkName: 'Shenandoah Blue Ridge & Monticello Dome', primaryColor: '#2563eb', secondaryColor: '#b91c1c', accentColor: '#f8fafc', archetype: 'CAPITOL_MONUMENT' },
  WA: { code: 'WA', stateName: 'Washington', landmarkName: 'Space Needle & Mount Rainier', primaryColor: '#0284c7', secondaryColor: '#059669', accentColor: '#f97316', archetype: 'SPACE_TOWER' },
  WV: { code: 'WV', stateName: 'West Virginia', landmarkName: 'New River Gorge Steel Arch Bridge', primaryColor: '#15803d', secondaryColor: '#eab308', accentColor: '#94a3b8', archetype: 'BRIDGE_ARCH' },
  WI: { code: 'WI', stateName: 'Wisconsin', landmarkName: 'Madison Capitol Dome & Door County Lighthouse', primaryColor: '#dc2626', secondaryColor: '#eab308', accentColor: '#f8fafc', archetype: 'CAPITOL_MONUMENT' },
  WY: { code: 'WY', stateName: 'Wyoming', landmarkName: 'Old Faithful Geyser & Grand Teton Peaks', primaryColor: '#0284c7', secondaryColor: '#f59e0b', accentColor: '#f8fafc', archetype: 'MOUNTAIN_CANYON' },
  DC: { code: 'DC', stateName: 'District of Columbia', landmarkName: 'U.S. Capitol Dome & Washington Monument', primaryColor: '#2563eb', secondaryColor: '#dc2626', accentColor: '#f8fafc', archetype: 'CAPITOL_MONUMENT' },
};

/**
 * Custom interior SVG artwork per state so each of the 51 jurisdictions depicts its true landmark.
 */
const BESPOKE_STATE_MOTIFS: Record<StateCode51, string> = {
  // NY: Statue of Liberty (patina crown, robe, raised golden torch) + Empire State spire
  NY: `
    <!-- Empire State Spire in background left -->
    <rect x="11" y="16" width="5.5" height="20.5" rx="0.8" fill="url(#lm_ny_acc)" stroke="#1e1b2e" stroke-width="1.2"/>
    <rect x="12.3" y="12" width="3" height="4.5" fill="#cbd5e1" stroke="#1e1b2e" stroke-width="1.1"/>
    <line x1="13.8" y1="7" x2="13.8" y2="12" stroke="#f8fafc" stroke-width="1.3" stroke-linecap="round"/>
    <!-- Liberty Pedestal & Patina Statue -->
    <path d="M20 30H33L34.5 36.5H18.5Z" fill="#cbd5e1" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M22 30L23.5 16.5H29.5L31 30Z" fill="url(#lm_ny_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Crown & Head -->
    <circle cx="26.5" cy="14" r="3" fill="#5eead4" stroke="#1e1b2e" stroke-width="1.25"/>
    <path d="M22.5 12.5L24 10M26.5 11V8.2M30.5 12.5L29 10" stroke="#2dd4bf" stroke-width="1.3" stroke-linecap="round"/>
    <!-- Raised Torch Arm & Flame -->
    <path d="M29.2 17.5L33 11.2" stroke="url(#lm_ny_main)" stroke-width="2.3" stroke-linecap="round"/>
    <path d="M33 11.2C31.5 9.5 32.5 6.8 34 6C35 7.5 35.5 9.8 33 11.2Z" fill="#facc15" stroke="#ea580c" stroke-width="1.1"/>
  `,

  // CA: Golden Gate Bridge (International Orange twin H-towers, catenary cables, bay water)
  CA: `
    <path d="M8 33C14 31.5 19 34.5 24 33C29 31.5 34 34.5 40 33L39 37H9Z" fill="#0284c7" stroke="#1e1b2e" stroke-width="1.2"/>
    <rect x="13" y="10" width="4.2" height="25.5" rx="1" fill="url(#lm_ca_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="30.8" y="10" width="4.2" height="25.5" rx="1" fill="url(#lm_ca_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <line x1="13" y1="15.5" x2="17.2" y2="15.5" stroke="#fecdd3" stroke-width="1.4"/>
    <line x1="30.8" y1="15.5" x2="35" y2="15.5" stroke="#fecdd3" stroke-width="1.4"/>
    <path d="M7.5 19Q15 26.5 15.1 11.5Q24 27.5 32.9 11.5Q33 26.5 40.5 19" stroke="#fb7185" stroke-width="1.6" fill="none"/>
    <line x1="20" y1="18.5" x2="20" y2="26.5" stroke="#fda4af" stroke-width="1.1"/>
    <line x1="24" y1="20" x2="24" y2="26.5" stroke="#fda4af" stroke-width="1.1"/>
    <line x1="28" y1="18.5" x2="28" y2="26.5" stroke="#fda4af" stroke-width="1.1"/>
    <path d="M7 26.5H41" stroke="#facc15" stroke-width="2.4" stroke-linecap="round"/>
  `,

  // TX: The Alamo Mission stepped arched facade + Lone Star & Longhorn horns
  TX: `
    <path d="M10 36.5V21H15V17.5C15 17.5 19.5 17.5 20.5 14.5C21.8 11.5 26.2 11.5 27.5 14.5C28.5 17.5 33 17.5 33 17.5V21H38V36.5Z" fill="url(#lm_tx_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M20.5 36.5V28.5C20.5 26 27.5 26 27.5 28.5V36.5Z" fill="#451a03" stroke="#1e1b2e" stroke-width="1.25"/>
    <rect x="13" y="25" width="3.5" height="5" rx="0.8" fill="#78350f" stroke="#1e1b2e" stroke-width="1.1"/>
    <rect x="31.5" y="25" width="3.5" height="5" rx="0.8" fill="#78350f" stroke="#1e1b2e" stroke-width="1.1"/>
    <!-- Longhorn Horns & Golden Lone Star -->
    <path d="M7 11.5C11 11.5 14.5 16.5 19.5 17H28.5C33.5 16.5 37 11.5 41 11.5C38.5 15.5 34.5 19.2 28.5 19.5H19.5C13.5 19.2 9.5 15.5 7 11.5Z" fill="#fef3c7" stroke="#1e1b2e" stroke-width="1.25"/>
    <polygon points="24,14.5 25.4,17.8 29,18.2 26.3,20.5 27.1,24 24,22.1 20.9,24 21.7,20.5 19,18.2 22.6,17.8" fill="#facc15" stroke="#1e1b2e" stroke-width="1.1"/>
  `,

  // FL: Kennedy Space Rocket launching + tropical palm & pink flamingo
  FL: `
    <!-- Palm Tree left -->
    <path d="M12 36.5Q13 28 11 22" stroke="#92400e" stroke-width="2" stroke-linecap="round" fill="none"/>
    <path d="M11 22C7.5 21 6.5 24 6.5 24M11 22C8.5 18.5 12 17.5 12 17.5M11 22C14.5 19.5 16 22.5 16 22.5" stroke="#16a34a" stroke-width="1.8" stroke-linecap="round" fill="none"/>
    <!-- Space Rocket center -->
    <path d="M18.5 35L15.5 37H28.5L25.5 35Z" fill="url(#lm_fl_main)" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M19 35V14.5C19 10.5 25 10.5 25 14.5V35Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M19 14.5L22 7.5L25 14.5Z" fill="url(#lm_fl_main)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <circle cx="22" cy="19.5" r="2.2" fill="#38bdf8" stroke="#1e1b2e" stroke-width="1.15"/>
    <!-- Flamingo right -->
    <ellipse cx="33" cy="29" rx="4.2" ry="3" fill="url(#lm_fl_acc)" stroke="#1e1b2e" stroke-width="1.25"/>
    <path d="M35.5 27.5C37.8 23 35.5 19 33.5 21.2" stroke="#ec4899" stroke-width="2" stroke-linecap="round" fill="none"/>
    <line x1="32.5" y1="32" x2="32.5" y2="36.5" stroke="#fb7185" stroke-width="1.3"/>
  `,

  // WA: Seattle Space Needle + snow-capped Mount Rainier & evergreen pine
  WA: `
    <!-- Mt Rainier peak in background -->
    <path d="M7 36.5L15.5 21L23 36.5Z" fill="#64748b" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M12.2 27L15.5 21L18.6 27L16.8 25.8L15.5 27.5L14 25.8Z" fill="#f8fafc"/>
    <!-- Space Needle Tower -->
    <path d="M21 36.5L25 17.5H28.5L32.5 36.5" stroke="#e2e8f0" stroke-width="2.1" stroke-linecap="round" fill="none"/>
    <line x1="26.7" y1="17.5" x2="26.7" y2="36.5" stroke="#94a3b8" stroke-width="1.4"/>
    <ellipse cx="26.7" cy="16" rx="10.5" ry="3" fill="url(#lm_wa_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <ellipse cx="26.7" cy="14.2" rx="6.5" ry="1.8" fill="url(#lm_wa_acc)" stroke="#1e1b2e" stroke-width="1.2"/>
    <line x1="26.7" y1="6.5" x2="26.7" y2="12.5" stroke="#facc15" stroke-width="1.7" stroke-linecap="round"/>
  `,

  // AZ: Stratified Red Grand Canyon Butte + Saguaro Cactus
  AZ: `
    <circle cx="34" cy="13.5" r="4.2" fill="#fde047" stroke="#ca8a04" stroke-width="1.1"/>
    <!-- Layered Grand Canyon Red Rock Butte -->
    <path d="M7.5 36.5L11.5 19H21.5L25 25.5H31.5L35 36.5Z" fill="url(#lm_az_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="10" y1="24.5" x2="23.5" y2="24.5" stroke="#fde047" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="8.8" y1="30" x2="32.5" y2="30" stroke="#fed7aa" stroke-width="1.3" stroke-linecap="round"/>
    <!-- Giant Saguaro Cactus -->
    <path d="M35.5 36.5V17.5M31.5 23.5V20.5C31.5 23.5 35.5 23.5 35.5 23.5M39.5 25.5V21.5C39.5 25.5 35.5 25.5 35.5 25.5" stroke="#16a34a" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
  `,

  // CO: Jagged Snow-Crowned Rocky Mountains + Red Rocks Monoliths
  CO: `
    <path d="M7 36.5L17 12.5L26.5 36.5Z" fill="url(#lm_co_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M19.5 36.5L29.5 10L40.5 36.5Z" fill="url(#lm_co_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M13.5 20.5L17 12.5L20.5 20.5L18.2 19L17 21L15.5 19Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1"/>
    <path d="M25.5 19.5L29.5 10L33.8 19.5L31.5 17.5L29.5 20L27.5 17.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1"/>
    <!-- Red Rocks Slanted Monoliths at base -->
    <path d="M10 36.5L14 27.5L22 31.5L20 36.5Z" fill="url(#lm_co_acc)" stroke="#1e1b2e" stroke-width="1.25" stroke-linejoin="round"/>
    <path d="M26 36.5L31 28.5L38 32L36 36.5Z" fill="url(#lm_co_acc)" stroke="#1e1b2e" stroke-width="1.25" stroke-linejoin="round"/>
  `,

  // HI: Diamond Head Volcanic Crater + Curling Waikiki Wave & Palm
  HI: `
    <circle cx="35" cy="13" r="4" fill="#facc15" stroke="#ea580c" stroke-width="1.1"/>
    <!-- Diamond Head Volcanic Tuff Cone -->
    <path d="M7.5 34.5L13 21.5L19.5 23.5L26.5 20.5L34 34.5Z" fill="url(#lm_hi_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Curling Turquoise Ocean Wave -->
    <path d="M8 36.5C15 36.5 18 29.5 25 29.5C29 29.5 30 33 27 33.5C32 34.5 37 32 40.5 36.5Z" fill="#06b6d4" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Hibiscus & Palm -->
    <circle cx="35.5" cy="27.5" r="3.5" fill="url(#lm_hi_acc)" stroke="#1e1b2e" stroke-width="1.2"/>
    <circle cx="35.5" cy="27.5" r="1.2" fill="#fef08a"/>
  `,

  // IL: Chicago Stepped Willis Tower with Twin Antennas + Silver Cloud Gate ("The Bean")
  IL: `
    <!-- Willis Tower stepped skyscraper -->
    <rect x="13" y="13" width="10" height="23.5" rx="0.8" fill="url(#lm_il_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="10.5" y="20" width="3" height="16.5" fill="#1e3a8a" stroke="#1e1b2e" stroke-width="1.2"/>
    <line x1="16" y1="6.8" x2="16" y2="13" stroke="#f8fafc" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="20" y1="6.8" x2="20" y2="13" stroke="#f8fafc" stroke-width="1.5" stroke-linecap="round"/>
    <!-- Cloud Gate ("The Bean") kidney arch foreground right -->
    <path d="M21 34C21 26.5 39 26.5 39 34C39 37 34.5 36.8 33 34.2C31.5 32 28.5 32 27 34.2C25.5 36.8 21 37 21 34Z" fill="#e2e8f0" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M24 30.5C27.5 28.2 33.5 28.2 36 30.5" stroke="#ffffff" stroke-width="1.4" stroke-linecap="round" fill="none"/>
  `,

  // LA: French Quarter Wrought-Iron Balcony, Bourbon Street Lamp & Mississippi Steamboat Paddlewheel
  LA: `
    <!-- Historic French Quarter Building & Wrought-Iron Balcony -->
    <rect x="10" y="13" width="16" height="23.5" rx="1" fill="url(#lm_la_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M8.5 24H27.5V28H8.5Z" fill="#1e1b2e" stroke="#facc15" stroke-width="1.2"/>
    <path d="M13 24V16.5C13 15 16.5 15 16.5 16.5V24M19.5 24V16.5C19.5 15 23 15 23 16.5V24" fill="#fef08a" stroke="#1e1b2e" stroke-width="1.1"/>
    <!-- Jazz Brass Horn / Steamboat Paddlewheel right -->
    <circle cx="33.5" cy="29.5" r="6" fill="#dc2626" stroke="#1e1b2e" stroke-width="1.35"/>
    <circle cx="33.5" cy="29.5" r="2.2" fill="#facc15" stroke="#1e1b2e" stroke-width="1.1"/>
    <path d="M33.5 23.5V35.5M27.5 29.5H39.5" stroke="#fde047" stroke-width="1.2"/>
  `,

  // MA: Boston Old North Church White Colonial Steeple & Harbor Lighthouse
  MA: `
    <!-- Old North Church Steeple & Paul Revere Lantern -->
    <rect x="13" y="23" width="11" height="13.5" fill="url(#lm_ma_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="15.5" y="16" width="6" height="7" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.25"/>
    <path d="M15 16L18.5 7L22 16Z" fill="#e2e8f0" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <circle cx="18.5" cy="19.5" r="1.8" fill="#facc15" stroke="#1e1b2e" stroke-width="1"/>
    <!-- Boston Harbor Lighthouse right -->
    <path d="M30 36.5L31.5 18H35.5L37 36.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <rect x="31" y="14.5" width="5" height="3.5" fill="#dc2626" stroke="#1e1b2e" stroke-width="1.2"/>
  `,

  // MO: St. Louis Gateway Arch (sleek stainless-steel parabolic catenary arch)
  MO: `
    <!-- Riverfront skyline blocks -->
    <rect x="15" y="27" width="4.5" height="9.5" fill="url(#lm_mo_acc)" stroke="#1e1b2e" stroke-width="1.1"/>
    <rect x="28.5" y="25" width="4.5" height="11.5" fill="url(#lm_mo_acc)" stroke="#1e1b2e" stroke-width="1.1"/>
    <!-- Parabolic Gateway Arch -->
    <path d="M9.5 36.5C11.5 7 36.5 7 38.5 36.5H33.8C32.2 13.5 15.8 13.5 14.2 36.5Z" fill="url(#lm_mo_main)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <path d="M12 34C14 10.5 34 10.5 36 34" stroke="#ffffff" stroke-width="1.1" stroke-linecap="round" fill="none" opacity="0.85"/>
  `,

  // NV: Welcome to Las Vegas Neon Diamond Sign + Hoover Dam Concrete Arch
  NV: `
    <!-- Hoover Dam curved canyon wall -->
    <path d="M8 21H24L21 36.5H11Z" fill="#cbd5e1" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M12 21C14.5 26 19.5 26 22 21" stroke="#38bdf8" stroke-width="2" fill="none"/>
    <!-- Fabulous Las Vegas Neon Diamond Sign -->
    <line x1="32" y1="14" x2="32" y2="36.5" stroke="#facc15" stroke-width="2.2"/>
    <polygon points="32,13 41.5,21 32,29 22.5,21" fill="url(#lm_nv_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <polygon points="32,15.5 38.5,21 32,26.5 25.5,21" fill="#fef08a" stroke="#1e1b2e" stroke-width="1"/>
    <!-- Neon Starburst top -->
    <circle cx="32" cy="9.5" r="2.5" fill="#facc15" stroke="#ec4899" stroke-width="1.2"/>
  `,

  // OR: Crater Lake Caldera with Wizard Island Cone + Snow-Capped Mt. Hood
  OR: `
    <!-- Mt Hood Peak background -->
    <path d="M14 27L24 9.5L34 27Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Crater Lake Caldera Rim & Deep Sapphire Water -->
    <path d="M7.5 36.5L10.5 24.5L17 27H31L37.5 24.5L40.5 36.5Z" fill="url(#lm_or_acc)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <ellipse cx="24" cy="30.5" rx="12" ry="4.5" fill="url(#lm_or_main)" stroke="#1e1b2e" stroke-width="1.3"/>
    <!-- Wizard Island cinder cone inside lake -->
    <path d="M19.5 31.5L23.5 26L27.5 31.5Z" fill="#15803d" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
  `,

  // PA: Philadelphia Liberty Bell with Timber Yoke & Iconic Crack
  PA: `
    <!-- Timber Yoke Beam -->
    <rect x="13" y="10" width="22" height="4.5" rx="1.2" fill="#78350f" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="21.5" y="14.5" width="5" height="3" fill="#94a3b8" stroke="#1e1b2e" stroke-width="1.2"/>
    <!-- Cast Bronze Liberty Bell -->
    <path d="M16.5 17.5H31.5L34 29.5L37.5 33.5H10.5L14 29.5Z" fill="url(#lm_pa_main)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- Iconic Liberty Bell Crack -->
    <path d="M24.5 20.5L23 25L25.5 28.5L24 33.5" stroke="#1e1b2e" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <!-- Bell Clapper -->
    <circle cx="24" cy="35" r="2.3" fill="#b45309" stroke="#1e1b2e" stroke-width="1.2"/>
  `,

  // SD: Mount Rushmore Granite Cliff with 4 Sculpted Profiles
  SD: `
    <path d="M7.5 36.5L10 14L18 11.5L30 12L38 15L40.5 36.5Z" fill="url(#lm_sd_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Four Sculpted Presidential Relief Heads -->
    <ellipse cx="15" cy="20" rx="2.8" ry="3.8" fill="#e7e5e4" stroke="#1e1b2e" stroke-width="1.25"/>
    <ellipse cx="21" cy="20.8" rx="2.8" ry="3.8" fill="#d6d3d1" stroke="#1e1b2e" stroke-width="1.25"/>
    <ellipse cx="27" cy="21" rx="2.8" ry="3.8" fill="#e7e5e4" stroke="#1e1b2e" stroke-width="1.25"/>
    <ellipse cx="33" cy="20.2" rx="2.8" ry="3.8" fill="#d6d3d1" stroke="#1e1b2e" stroke-width="1.25"/>
    <!-- Black Hills Pine Line at base -->
    <path d="M9 36.5L12 30.5L15 36.5L18 29.5L21 36.5L27 30L30 36.5L34 29.5L38 36.5Z" fill="url(#lm_sd_acc)" stroke="#1e1b2e" stroke-width="1.15" stroke-linejoin="round"/>
  `,

  // UT: Delicate Arch (freestanding natural red-orange Entrada sandstone arch)
  UT: `
    <circle cx="24" cy="23" r="4" fill="#fde047" opacity="0.85"/>
    <!-- Natural Sandstone Delicate Arch -->
    <path d="M9.5 36.5L11.5 18.5C12.5 10.5 35.5 10.5 36.5 18.5L38.5 36.5H30.5L29.5 23.5C29 17.5 19 17.5 18.5 23.5L17.5 36.5Z" fill="url(#lm_ut_main)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- Sandstone geological strata & highlight -->
    <path d="M13 18.5C17 13.5 31 13.5 35 18.5" stroke="#fed7aa" stroke-width="1.4" stroke-linecap="round" fill="none"/>
    <line x1="10.5" y1="28" x2="18" y2="27" stroke="#9a3412" stroke-width="1.2"/>
    <line x1="30" y1="27" x2="37.5" y2="28" stroke="#9a3412" stroke-width="1.2"/>
  `,

  // DC: U.S. Capitol Neoclassical Dome + Washington Monument Marble Obelisk
  DC: `
    <!-- U.S. Capitol Dome left/center -->
    <rect x="9" y="27.5" width="21" height="9" rx="1" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M12.5 27.5C12.5 17 26.5 17 26.5 27.5Z" fill="url(#lm_dc_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <line x1="19.5" y1="13" x2="19.5" y2="18" stroke="#facc15" stroke-width="1.8" stroke-linecap="round"/>
    <line x1="13" y1="29.5" x2="13" y2="35" stroke="#64748b" stroke-width="1.3"/>
    <line x1="17" y1="29.5" x2="17" y2="35" stroke="#64748b" stroke-width="1.3"/>
    <line x1="22" y1="29.5" x2="22" y2="35" stroke="#64748b" stroke-width="1.3"/>
    <line x1="26" y1="29.5" x2="26" y2="35" stroke="#64748b" stroke-width="1.3"/>
    <!-- Washington Monument Obelisk right -->
    <path d="M33 36.5L34 11.5L36 7.5L38 11.5L39 36.5Z" fill="#f1f5f9" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
  `,

  // AL: Huntsville Saturn V Rocket + Edmund Pettus Arch Bridge
  AL: `
    <path d="M9 36.5C13 26.5 25 26.5 29 36.5" stroke="url(#lm_al_acc)" stroke-width="2.5" fill="none"/>
    <path d="M9 33.5H29" stroke="#cbd5e1" stroke-width="1.8"/>
    <rect x="29" y="13" width="6" height="23.5" rx="1" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M29 13L32 7L35 13Z" fill="url(#lm_al_main)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <rect x="29" y="21" width="6" height="4" fill="#1e293b"/>
  `,

  // AK: Denali Twin Snow Peaks + Shimmering Green/Violet Aurora Borealis Ribbon
  AK: `
    <path d="M9 14C16 9 22 18 29 13C34 9.5 38 14 40 12" stroke="#34d399" stroke-width="2.6" stroke-linecap="round" fill="none"/>
    <path d="M11 18C18 13.5 24 20.5 32 16" stroke="url(#lm_ak_acc)" stroke-width="1.8" stroke-linecap="round" fill="none"/>
    <path d="M7.5 36.5L18.5 14L28 36.5Z" fill="url(#lm_ak_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M19 36.5L30.5 11.5L40.5 36.5Z" fill="url(#lm_ak_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M26.5 20L30.5 11.5L34.5 20L32 18.2L30.5 20.5L28.8 18.2Z" fill="#f8fafc"/>
  `,

  // AR: Hot Springs Bathhouse Row + Crater of Diamonds Crystal
  AR: `
    <rect x="10" y="21" width="18" height="15.5" rx="1" fill="url(#lm_ar_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M9 21L19 14L29 21Z" fill="url(#lm_ar_acc)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <polygon points="34.5,14 40,21 34.5,31 29,21" fill="#e0f2fe" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <line x1="29" y1="21" x2="40" y2="21" stroke="#38bdf8" stroke-width="1.2"/>
  `,

  // CT: Mystic Seaport Tall Ship Sail + Stone Harbor Lighthouse
  CT: `
    <path d="M11 36.5L12.8 15H17.2L19 36.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <circle cx="15" cy="12.5" r="2.4" fill="#facc15" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M24 32H39L36.5 36.5H26.5Z" fill="#78350f" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M31 32V13L39 29H31Z" fill="url(#lm_ct_main)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M29.5 30V16L23.5 29H29.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.25" stroke-linejoin="round"/>
  `,

  // DE: Delaware Memorial Twin Suspension Bridge & Old New Castle Cupola
  DE: `
    <rect x="12.5" y="11" width="4" height="25.5" rx="0.8" fill="url(#lm_de_main)" stroke="#1e1b2e" stroke-width="1.3"/>
    <rect x="31.5" y="11" width="4" height="25.5" rx="0.8" fill="url(#lm_de_main)" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M7.5 20Q14.5 27 14.5 13Q24 28 33.5 13Q33.5 27 40.5 20" stroke="#93c5fd" stroke-width="1.6" fill="none"/>
    <path d="M7 27H41" stroke="#facc15" stroke-width="2.3" stroke-linecap="round"/>
  `,

  // GA: Stone Mountain Granite Dome + Georgia Peach Emblem
  GA: `
    <path d="M7.5 36.5C9 21 22 17 31 25L35 36.5Z" fill="#94a3b8" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <circle cx="31.5" cy="24.5" r="7" fill="url(#lm_ga_main)" stroke="#1e1b2e" stroke-width="1.4"/>
    <path d="M31.5 17.5C34 14 38 15 37 18.5C35 19.5 32.5 19 31.5 17.5Z" fill="#16a34a" stroke="#1e1b2e" stroke-width="1.2"/>
  `,

  // ID: Shoshone Falls Cataract & Sawtooth Mountain Peaks
  ID: `
    <path d="M8 26L16 12L24 26L32 11L40 26Z" fill="url(#lm_id_acc)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <rect x="9" y="25" width="30" height="4" rx="1" fill="#78716c" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M14 25V36.5H34V25Z" fill="url(#lm_id_main)" stroke="#1e1b2e" stroke-width="1.3"/>
    <line x1="19" y1="26.5" x2="19" y2="35" stroke="#e0f2fe" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="24" y1="26.5" x2="24" y2="35" stroke="#e0f2fe" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="29" y1="26.5" x2="29" y2="35" stroke="#e0f2fe" stroke-width="1.4" stroke-linecap="round"/>
  `,

  // IN: Indianapolis Speedway Control Pagoda & Checkered Flag Arch
  IN: `
    <rect x="17" y="24" width="14" height="12.5" fill="url(#lm_in_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="19" y="17.5" width="10" height="6.5" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.25"/>
    <rect x="21" y="12" width="6" height="5.5" fill="url(#lm_in_acc)" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M15 24H33M17.5 17.5H30.5M19.5 12H28.5" stroke="#1e1b2e" stroke-width="2" stroke-linecap="round"/>
    <line x1="24" y1="7" x2="24" y2="12" stroke="#facc15" stroke-width="1.6" stroke-linecap="round"/>
  `,

  // IA: Des Moines Golden Capitol Dome & RedGambrel Heartland Barn
  IA: `
    <path d="M9 36.5V25L13 19.5L19 18L25 19.5L29 25V36.5Z" fill="url(#lm_ia_acc)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M14.5 36.5V28.5H23.5V36.5 M14.5 28.5L23.5 36.5 M23.5 28.5L14.5 36.5" stroke="#f8fafc" stroke-width="1.2"/>
    <path d="M27 26C27 16 39 16 39 26Z" fill="url(#lm_ia_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="27" y="26" width="12" height="10.5" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.25"/>
  `,

  // KS: Monument Rocks Chalk Pyramids & Golden Sunflower
  KS: `
    <path d="M9 36.5L12 18H18L20.5 36.5Z" fill="#fef3c7" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <circle cx="31" cy="21" r="7.5" fill="url(#lm_ks_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <circle cx="31" cy="21" r="3.6" fill="#78350f" stroke="#1e1b2e" stroke-width="1.2"/>
    <line x1="31" y1="28.5" x2="31" y2="36.5" stroke="#16a34a" stroke-width="2.4"/>
  `,

  // KY: Churchill Downs Twin Spires & Golden Horseshoe
  KY: `
    <rect x="11" y="25" width="26" height="11.5" fill="url(#lm_ky_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M12.5 25L16 11L19.5 25Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M28.5 25L32 11L35.5 25Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M20.5 22C20.5 16.5 27.5 16.5 27.5 22" stroke="#facc15" stroke-width="2.2" stroke-linecap="round" fill="none"/>
  `,

  // ME: Portland Head Light on Rocky Cliff & Acadia Pine
  ME: `
    <path d="M8 36.5L13 24.5L19 36.5Z" fill="#15803d" stroke="#1e1b2e" stroke-width="1.25" stroke-linejoin="round"/>
    <path d="M22 36.5L24.5 14H30.5L33 36.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <rect x="24" y="10.5" width="7" height="3.8" rx="0.8" fill="url(#lm_me_acc)" stroke="#1e1b2e" stroke-width="1.25"/>
    <circle cx="27.5" cy="12.4" r="1.5" fill="#fef08a"/>
  `,

  // MD: Chesapeake Screw-Pile Hexagonal Lighthouse & Star Fort Rampart
  MD: `
    <polygon points="24,11 33,17.5 33,27 15,27 15,17.5" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M13.5 17.5L24 10L34.5 17.5Z" fill="url(#lm_md_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <line x1="16" y1="27" x2="12" y2="36.5" stroke="#1e1b2e" stroke-width="1.8"/>
    <line x1="24" y1="27" x2="24" y2="36.5" stroke="#1e1b2e" stroke-width="1.8"/>
    <line x1="32" y1="27" x2="36" y2="36.5" stroke="#1e1b2e" stroke-width="1.8"/>
  `,

  // MI: Mighty Mackinac Suspension Bridge over Turquoise Great Lakes
  MI: `
    <path d="M8 32.5H40L38 36.5H10Z" fill="url(#lm_mi_main)" stroke="#1e1b2e" stroke-width="1.2"/>
    <rect x="13.5" y="10.5" width="4" height="24" rx="0.8" fill="url(#lm_mi_acc)" stroke="#1e1b2e" stroke-width="1.3"/>
    <rect x="30.5" y="10.5" width="4" height="24" rx="0.8" fill="url(#lm_mi_acc)" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M7.5 19Q15.5 26 15.5 12.5Q24 27.5 32.5 12.5Q32.5 26 40.5 19" stroke="#f8fafc" stroke-width="1.6" fill="none"/>
    <path d="M7 26.5H41" stroke="#22c55e" stroke-width="2.4" stroke-linecap="round"/>
  `,

  // MN: Split Rock Cliffside Brick Lighthouse over Lake Superior
  MN: `
    <path d="M8 36.5L12 24H28L31 36.5Z" fill="#78716c" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M16.5 24L18 11.5H23L24.5 24Z" fill="url(#lm_mn_acc)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <circle cx="20.5" cy="9.5" r="2.3" fill="#fef08a" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M23 9.5L38 6.5V12.5Z" fill="rgba(254, 240, 138, 0.45)"/>
  `,

  // MS: Mississippi River Paddlewheeler Steamboat & Twin Smokestacks
  MS: `
    <path d="M9 30.5H39L36.5 36.5H11.5Z" fill="url(#lm_ms_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <rect x="13" y="22.5" width="18" height="8" rx="1" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.3"/>
    <line x1="17" y1="11.5" x2="17" y2="22.5" stroke="#1e1b2e" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="23" y1="11.5" x2="23" y2="22.5" stroke="#1e1b2e" stroke-width="2.4" stroke-linecap="round"/>
    <circle cx="33.5" cy="29.5" r="5" fill="url(#lm_ms_acc)" stroke="#1e1b2e" stroke-width="1.35"/>
  `,

  // MT: Glacier National Park Going-to-the-Sun Triple Crown Peaks
  MT: `
    <path d="M7 36.5L15 15L23 36.5Z" fill="url(#lm_mt_acc)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M16 36.5L25.5 10L35 36.5Z" fill="url(#lm_mt_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M27 36.5L35 17L41 36.5Z" fill="url(#lm_mt_acc)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M22 19L25.5 10L29 19L27 17.2L25.5 19.5L24 17.2Z" fill="#f8fafc"/>
  `,

  // NE: Chimney Rock Needle Spire rising from Prairie Bluff
  NE: `
    <path d="M8.5 36.5L14 25.5H34L39.5 36.5Z" fill="url(#lm_ne_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M21.5 25.5L22.8 9.5H25.2L26.5 25.5Z" fill="url(#lm_ne_acc)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <circle cx="34" cy="13.5" r="3.8" fill="#fef08a" stroke="#d97706" stroke-width="1.1"/>
  `,

  // NH: Mount Washington Alpine Peak & Historic Trestle
  NH: `
    <path d="M8 36.5L22 10.5L38 36.5Z" fill="url(#lm_nh_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M17.5 19L22 10.5L26.8 19L24.2 17.2L22 19.5L19.8 17.2Z" fill="#f8fafc"/>
    <path d="M10 33L25 18" stroke="#facc15" stroke-width="2.2" stroke-dasharray="2.5 2" stroke-linecap="round"/>
  `,

  // NJ: Cape May Red-Capped Lighthouse & Boardwalk Ferris Wheel
  NJ: `
    <circle cx="17" cy="24.5" r="7.5" stroke="url(#lm_nj_main)" stroke-width="1.6" fill="none"/>
    <circle cx="17" cy="24.5" r="2" fill="#facc15" stroke="#1e1b2e" stroke-width="1.1"/>
    <path d="M13 36.5L17 24.5L21 36.5" stroke="#1e1b2e" stroke-width="1.4" fill="none"/>
    <path d="M27.5 36.5L29.5 13.5H34.5L36.5 36.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="29" y="10" width="6" height="3.8" fill="url(#lm_nj_acc)" stroke="#1e1b2e" stroke-width="1.2"/>
  `,

  // NM: Taos Pueblo Stepped Adobe Terraces & Red Zia Sun Symbol
  NM: `
    <circle cx="24" cy="13" r="3.2" fill="#fde047" stroke="#dc2626" stroke-width="1.3"/>
    <path d="M24 7V19M18 13H30" stroke="#dc2626" stroke-width="1.6" stroke-linecap="round"/>
    <rect x="9.5" y="27.5" width="29" height="9" rx="1" fill="url(#lm_nm_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <rect x="14" y="21" width="20" height="6.5" rx="1" fill="url(#lm_nm_main)" stroke="#1e1b2e" stroke-width="1.35"/>
    <line x1="17" y1="19" x2="15" y2="27.5" stroke="#78350f" stroke-width="1.5"/>
  `,

  // NC: Cape Hatteras Diagonal Black-and-White Striped Lighthouse & Wright Flyer
  NC: `
    <path d="M19 36.5L21.5 12.5H26.5L29 36.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M21 18L27 22M20.2 25L27.8 29.5M19.5 31.5L28.2 35.5" stroke="#1e293b" stroke-width="2.6"/>
    <circle cx="24" cy="10" r="2.4" fill="#facc15" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M9 16H17M10 18.5H16" stroke="url(#lm_nc_main)" stroke-width="1.6" stroke-linecap="round"/>
  `,

  // ND: Theodore Roosevelt Painted Canyon Badlands Buttes
  ND: `
    <path d="M7.5 36.5L11 21H20L24 27.5L28 19H36L40.5 36.5Z" fill="url(#lm_nd_main)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="10" y1="26" x2="21.5" y2="26" stroke="#fde047" stroke-width="1.4"/>
    <line x1="27" y1="24.5" x2="37.5" y2="24.5" stroke="#fde047" stroke-width="1.4"/>
    <line x1="8.8" y1="31.5" x2="39" y2="31.5" stroke="#22c55e" stroke-width="1.5"/>
  `,

  // OH: Cleveland Rock & Roll Hall of Fame Glass Pyramid & Guitar Neck
  OH: `
    <polygon points="24,11 39,36.5 9,36.5" fill="url(#lm_oh_acc)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="24" y1="11" x2="24" y2="36.5" stroke="#1e1b2e" stroke-width="1.2"/>
    <line x1="16.5" y1="24" x2="31.5" y2="24" stroke="#f8fafc" stroke-width="1.2"/>
    <rect x="21.5" y="7.5" width="5" height="12" rx="1" fill="url(#lm_oh_main)" stroke="#1e1b2e" stroke-width="1.25"/>
  `,

  // OK: Red Earth Oil Derrick Lattice Tower & Golden Gusher
  OK: `
    <path d="M16.5 36.5L22 12H26L31.5 36.5" stroke="url(#lm_ok_main)" stroke-width="2.2" stroke-linecap="round" fill="none"/>
    <path d="M19.5 20H28.5M18 28H30M19.5 20L30 28M28.5 20L18 28" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M24 12C21 7.5 16 7 14 9.5M24 12C27 7.5 32 7 34 9.5" stroke="#facc15" stroke-width="2.2" stroke-linecap="round" fill="none"/>
  `,

  // RI: Newport Pell Suspension Bridge & Coastal Cliff Lighthouse
  RI: `
    <rect x="12" y="13" width="3.8" height="23.5" fill="url(#lm_ri_main)" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M7.5 26.5H31" stroke="#38bdf8" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M7.5 20Q14 26 14 14Q21 26 29 20" stroke="#f8fafc" stroke-width="1.4" fill="none"/>
    <path d="M31 36.5L32.8 16H37.2L39 36.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <circle cx="35" cy="13.5" r="2.2" fill="#facc15" stroke="#1e1b2e" stroke-width="1.1"/>
  `,

  // SC: Fort Sumter Coastal Rampart, Palmetto Tree & Crescent Moon
  SC: `
    <path d="M14 11C11.5 12.5 11.5 16.5 14 18C10 18 9 13 14 11Z" fill="#f8fafc"/>
    <rect x="9" y="28.5" width="30" height="8" rx="1" fill="url(#lm_sc_acc)" stroke="#1e1b2e" stroke-width="1.35"/>
    <line x1="25" y1="30" x2="25" y2="16.5" stroke="#78350f" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M25 16.5C19 15 18 19.5 18 19.5M25 16.5C21 11.5 25 10.5 25 10.5M25 16.5C29 11.5 31 15 31 15M25 16.5C31 16 32 20 32 20" stroke="#22c55e" stroke-width="2" stroke-linecap="round" fill="none"/>
  `,

  // TN: Great Smoky Mountains Mist Ridges & Nashville Twin Spires
  TN: `
    <path d="M7.5 36.5L16.5 19.5L26 36.5Z" fill="url(#lm_tn_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <rect x="25" y="16" width="11" height="20.5" rx="0.8" fill="url(#lm_tn_acc)" stroke="#1e1b2e" stroke-width="1.35"/>
    <line x1="27" y1="9" x2="27" y2="16" stroke="#f8fafc" stroke-width="1.7" stroke-linecap="round"/>
    <line x1="34" y1="9" x2="34" y2="16" stroke="#f8fafc" stroke-width="1.7" stroke-linecap="round"/>
  `,

  // VT: Red Wooden Covered Truss Bridge & Green Mountains
  VT: `
    <path d="M8 31L18 13.5L28 31L34 17.5L40 31Z" fill="url(#lm_vt_main)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <rect x="10" y="25.5" width="28" height="9.5" fill="url(#lm_vt_acc)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M8.5 25.5L24 18.5L39.5 25.5Z" fill="#991b1b" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <rect x="18" y="28" width="12" height="7" fill="#1e1b2e"/>
  `,

  // VA: Monticello Octagonal Neoclassical Dome & Blue Ridge Peaks
  VA: `
    <path d="M8 28L17 14.5L26 28Z" fill="url(#lm_va_main)" stroke="#1e1b2e" stroke-width="1.25" stroke-linejoin="round"/>
    <rect x="12" y="25.5" width="25" height="11" rx="1" fill="url(#lm_va_acc)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M17.5 25.5C17.5 17.5 31.5 17.5 31.5 25.5Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M19 25.5L24.5 21L30 25.5Z" fill="#e2e8f0" stroke="#1e1b2e" stroke-width="1.2"/>
  `,

  // WV: New River Gorge Single-Span Steel Arch Bridge over Forest Canyon
  WV: `
    <path d="M7.5 22L14 36.5H8ZM40.5 22L34 36.5H40Z" fill="url(#lm_wv_main)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M8 21.5H40" stroke="url(#lm_wv_acc)" stroke-width="2.6" stroke-linecap="round"/>
    <path d="M10 34.5C15 21 33 21 38 34.5" stroke="#e2e8f0" stroke-width="2.2" fill="none"/>
    <line x1="18" y1="21.5" x2="18" y2="27" stroke="#cbd5e1" stroke-width="1.2"/>
    <line x1="24" y1="21.5" x2="24" y2="24.8" stroke="#cbd5e1" stroke-width="1.2"/>
    <line x1="30" y1="21.5" x2="30" y2="27" stroke="#cbd5e1" stroke-width="1.2"/>
  `,

  // WI: Madison White Granite Capitol Dome & Door County Red Lighthouse
  WI: `
    <rect x="10" y="26.5" width="18" height="10" rx="1" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M12.5 26.5C12.5 16 25.5 16 25.5 26.5Z" fill="#f1f5f9" stroke="#1e1b2e" stroke-width="1.35"/>
    <circle cx="19" cy="14" r="1.8" fill="#facc15" stroke="#1e1b2e" stroke-width="1"/>
    <path d="M31 36.5L32.5 17H36.5L38 36.5Z" fill="url(#lm_wi_main)" stroke="#1e1b2e" stroke-width="1.35"/>
  `,

  // WY: Old Faithful Erupting Geyser Plume & Grand Teton Jagged Peaks
  WY: `
    <path d="M8 36.5L16.5 14.5L24.5 36.5Z" fill="url(#lm_wy_main)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Old Faithful Geyser Cone & Erupting Steam Column -->
    <path d="M23 36.5L27 31.5H35L39 36.5Z" fill="url(#lm_wy_acc)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M31 31.5V12C28 12 26 9.5 28.5 7.5C30 6 33 6 34.5 7.5C37 9.5 34 12 31 12" fill="#f8fafc" stroke="#38bdf8" stroke-width="1.5" stroke-linecap="round"/>
  `,
};

function buildStateLandmarkSvg(meta: StateLandmarkMetadata): string {
  const idPrefix = `lm_${meta.code.toLowerCase()}`;
  const motif = BESPOKE_STATE_MOTIFS[meta.code];

  return `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="${idPrefix}_bg" x1="6" y1="4" x2="42" y2="42" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#1e293b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="${idPrefix}_main" x1="10" y1="8" x2="38" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="${meta.primaryColor}"/>
        <stop offset="100%" stop-color="${meta.secondaryColor}"/>
      </linearGradient>
      <linearGradient id="${idPrefix}_acc" x1="12" y1="10" x2="36" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="${meta.secondaryColor}"/>
        <stop offset="100%" stop-color="${meta.accentColor}"/>
      </linearGradient>
    </defs>
    <!-- Soft Ground Drop Shadow -->
    <ellipse cx="24" cy="43" rx="17" ry="3.2" fill="rgba(15, 23, 42, 0.45)"/>
    <!-- Isometric Diorama Frame Backdrop -->
    <rect x="5" y="4" width="38" height="36" rx="9" fill="url(#${idPrefix}_bg)" stroke="#1e1b2e" stroke-width="1.5"/>
    <path d="M9 7.5H35" stroke="rgba(255, 255, 255, 0.16)" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Bespoke 2.5D Landmark Illustration for ${meta.code} (${meta.landmarkName}) -->
    ${motif}
    <!-- 2.5D Tabletop Plinth & State Code Badge -->
    <path d="M6.5 36.5H41.5L39.5 41.5H8.5Z" fill="#334155" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <rect x="18.5" y="37.2" width="11" height="3.6" rx="1.2" fill="#facc15" stroke="#1e1b2e" stroke-width="0.9"/>
  </svg>`;
}

export const STATE_LANDMARK_SVG_ICONS: Record<StateCode51, string> = (
  Object.keys(STATE_LANDMARK_METADATA) as StateCode51[]
).reduce((acc, code) => {
  acc[code] = buildStateLandmarkSvg(STATE_LANDMARK_METADATA[code]);
  return acc;
}, {} as Record<StateCode51, string>);

const FIPS_TO_STATE_CODE: Record<string, StateCode51> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
  '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
  '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
  '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
  '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
  '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
  '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
  '56': 'WY',
};

const NAME_TO_STATE_CODE: Record<string, StateCode51> = (
  Object.values(STATE_LANDMARK_METADATA)
).reduce((acc, meta) => {
  acc[meta.stateName.toUpperCase()] = meta.code;
  return acc;
}, {} as Record<string, StateCode51>);

/**
 * Resolves the state landmark metadata for any 2-letter postal code, 2-digit FIPS code, or full state name.
 */
export function getStateLandmarkMetadata(stateCodeOrName: string): StateLandmarkMetadata {
  if (!stateCodeOrName) return STATE_LANDMARK_METADATA.CA;
  const cleaned = stateCodeOrName.trim().toUpperCase();

  if (cleaned in STATE_LANDMARK_METADATA) {
    return STATE_LANDMARK_METADATA[cleaned as StateCode51];
  }
  const paddedFips = cleaned.length === 1 && /^\d$/.test(cleaned) ? `0${cleaned}` : cleaned;
  if (paddedFips in FIPS_TO_STATE_CODE) {
    return STATE_LANDMARK_METADATA[FIPS_TO_STATE_CODE[paddedFips]];
  }
  if (cleaned in NAME_TO_STATE_CODE) {
    return STATE_LANDMARK_METADATA[NAME_TO_STATE_CODE[cleaned]];
  }
  for (const [name, code] of Object.entries(NAME_TO_STATE_CODE)) {
    if (cleaned.includes(name) || name.includes(cleaned)) {
      return STATE_LANDMARK_METADATA[code];
    }
  }
  return STATE_LANDMARK_METADATA.CA;
}

/**
 * Returns the bespoke 2.5D illustrated landmark SVG for any of the 50 US states + DC.
 */
export function getStateLandmarkIconSvg(
  stateCodeOrName: string,
  options?: IconRenderOptions
): string {
  const meta = getStateLandmarkMetadata(stateCodeOrName);
  const rawSvg = STATE_LANDMARK_SVG_ICONS[meta.code] || STATE_LANDMARK_SVG_ICONS.CA;
  return renderSvg(rawSvg, {
    size: 32,
    title: `${meta.stateName}: ${meta.landmarkName}`,
    ...options,
  });
}
